/**
 * DashboardHeader — sticky top bar with logo, theme/lang toggles, messages,
 * notifications, tour-help button and user dropdown menu (including subscription
 * usage strip and admin shortcuts).
 *
 * All state stays in the parent; this component only reads and emits events.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileText, MessageSquare, ChevronDown, User, Settings, CreditCard,
  Gift, Shield, Bug, LogOut,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSelector from "@/components/LanguageSelector";
import NotificationBell from "@/components/NotificationBell";
import MentionsInbox from "@/components/MentionsInbox";
import BugReportDialog from "@/components/BugReportDialog";
import { TourHelpButton } from "@/components/AppTour";

export default function DashboardHeader({
  t,
  user,
  logout,
  unreadDMCount,
  subscription,
  isAdmin,
  onProfileClick,
  onPreferencesClick,
  onStartTour,
}) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-lg sm:text-xl font-bold truncate">SlideCon</h1>
          </div>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2">
          <span className="hidden sm:inline-flex">
            <ThemeToggle variant="ghost" />
          </span>
          <span className="hidden sm:inline-flex">
            <LanguageSelector variant="ghost" />
          </span>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate("/messages")}
            data-testid="messages-nav-btn"
          >
            <MessageSquare className="w-5 h-5" />
            {unreadDMCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-rose-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1">
                {unreadDMCount > 99 ? "99+" : unreadDMCount}
              </span>
            )}
          </Button>

          <NotificationBell />
          <MentionsInbox />
          <TourHelpButton onClick={onStartTour} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>{user?.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  {user?.user_type && (
                    <Badge variant="outline" className="w-fit text-xs capitalize">
                      {user.user_type} {user.user_subcategory && `- ${user.user_subcategory}`}
                    </Badge>
                  )}
                </div>
              </DropdownMenuLabel>

              {subscription && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">{t("subscription_usage")}</span>
                      <Badge variant={subscription.subscription.tier === "free" ? "secondary" : "default"} className="text-xs capitalize">
                        {subscription.subscription.tier_name}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span>{t("projects")}</span>
                        <span className={subscription.usage.projects_count >= subscription.usage.projects_limit && subscription.usage.projects_limit !== -1 ? "text-red-500 font-medium" : ""}>
                          {subscription.usage.projects_count} / {subscription.usage.projects_limit === -1 ? "∞" : subscription.usage.projects_limit}
                        </span>
                      </div>
                      <Progress
                        value={subscription.usage.projects_limit === -1 ? 0 : (subscription.usage.projects_count / subscription.usage.projects_limit) * 100}
                        className="h-1"
                      />
                      <div className="flex items-center justify-between text-xs">
                        <span>{t("ai_generations")}</span>
                        <span className={subscription.usage.ai_generations_used >= subscription.usage.ai_generations_limit && subscription.usage.ai_generations_limit !== -1 ? "text-red-500 font-medium" : ""}>
                          {subscription.usage.ai_generations_used} / {subscription.usage.ai_generations_limit === -1 ? "∞" : subscription.usage.ai_generations_limit}
                        </span>
                      </div>
                      <Progress
                        value={subscription.usage.ai_generations_limit === -1 ? 0 : (subscription.usage.ai_generations_used / subscription.usage.ai_generations_limit) * 100}
                        className="h-1"
                      />
                    </div>
                  </div>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={onProfileClick} data-testid="profile-settings-btn">
                <User className="w-4 h-4" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={onPreferencesClick} data-testid="preferences-btn">
                <Settings className="w-4 h-4" />
                {t("preferences")}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => navigate("/pricing")} data-testid="pricing-btn">
                <CreditCard className="w-4 h-4" />
                {t("pricing_plans")}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => navigate("/collaborators")} data-testid="collaborators-btn">
                <Gift className="w-4 h-4" />
                {t("collaborator_program")}
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer text-amber-500 font-medium"
                    onClick={() => navigate("/admin")}
                    data-testid="admin-dashboard-btn"
                  >
                    <Shield className="w-4 h-4" />
                    Admin Dashboard
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <BugReportDialog
                trigger={
                  <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={(e) => e.preventDefault()}>
                    <Bug className="w-4 h-4" />
                    Report Bug
                  </DropdownMenuItem>
                }
              />
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 cursor-pointer text-red-500" onClick={logout}>
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
